import { AbstractControl, TouchedChangeEvent, ValidationErrors } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  computed,
  effect,
  inject,
  Injector,
  OnInit,
  afterNextRender,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { UserRolesService } from 'app/features/admin/user-roles/user-roles.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { UserStore } from 'app/core/services/user.store';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { debounceTime, filter, merge, takeUntil } from 'rxjs';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { DateHelperService } from '../../services/date-helper.service';
import type { RouteDuration, RoutePointTime, StopCardView, TravelSegment } from './route.types';
import { buildStopCardHeader, isStopFilled } from './route-stop-card.util';
import { StopCardComponent } from './route-stop-card.component';
import { RouteSectionSlot } from './route-section-slot.service';
import { hasMapCoordinates } from './route-section.util';
import { RouteMapService } from '../../../core/services/route-map.service';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { RouteMapComponent } from '../route-map.component';
import { RouteDateUtil } from './route.date.util';
import { FocusIntent, focusablesIn, intendedField, keepFocusOn } from './route.focus-keeper';
import { SortsOnTimePipe } from './route.sorts-on-time.pipe';
import { permutationForMove, rederiveDaysAfter, removeOneDay, reorderByArrival } from './route.reorder';
import { hasHiddenClass } from './readonly/overview.types';
import { commitUserValue, markUserEdit } from './user-edit.util';
import { UserPreferenceStore } from '../../../core/services/user-preference/user-preference.store';

/** Preference key holding the route date/time visibility setting. */
const ROUTE_SHOW_DATES_KEY = 'routeShowDates';

/**
 * The two halves of a stop's arrival moment.
 *
 * Date and time render as separate inputs but are one value to the planner, and the route sorts
 * on the pair. Watching only the time half means moving a stop to another day never reorders it —
 * and with the dates on screen that stop has no reorder controls either, so it is stranded where
 * it is.
 */
function isArrivalInput(input: HTMLInputElement | null): boolean {
  const name = input?.getAttribute('name');
  return name === 'time' || name === 'date';
}

/** A stop's model without the children hidden for this role (hideUserRole sets the `hidden` class): the card header must not read them out. */
function withoutRoleHidden(stop: FormlyFieldConfig): Record<string, unknown> {
  const hidden = (stop.fieldGroup ?? []).filter(child => hasHiddenClass(child.className)).map(child => String(child.key));
  const model = (stop.model ?? {}) as Record<string, unknown>;
  return hidden.length ? Object.fromEntries(Object.entries(model).filter(([key]) => !hidden.includes(key))) : model;
}

@Component({
  selector: 'formly-route-section',
  template: `
    <!-- In a route section the section carries the header, the departure/destination slots and
         the add button; this field keeps only the stops. -->
    @let canAddStop = !form.disabled && !field.props.disabled && !hideAddButton();
    <div [class.my-4]="!inRouteSection()" [attr.data-cy]="field.props?.keyPath ?? null">
      <!-- Header with label, total time and map toggle -->
      @if (props.label && !props.hideLabel && !inRouteSection()) {
        <div class="flex items-center justify-between mb-2">
          <h3 class="flex items-center gap-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
          <div class="flex items-center gap-2">
            <!-- Date visibility toggle, ahead of the read-only total and the map: it changes
                 what the whole block does, so it leads the group rather than trailing it. -->
            <button
              type="button"
              mat-stroked-button
              class="small-button dd-route-mode"
              (click)="toggleDates()"
              [attr.aria-describedby]="modeHintId"
              [matTooltip]="
                showDates()
                  ? 'Verbergt de datum. De dag volgt dan uit de volgorde van de stops, sorteren stopt.'
                  : 'Toont de datum bij elke stop. De route sorteert dan op datum en tijd.'
              ">
              <!-- The label names the ACTION, so no aria-pressed: on an action label every
                   pressed value is wrong for half the users. The state is announced through
                   aria-describedby on the line below the header instead — action and state
                   both, without contradiction. An aria-label carrying the state would break
                   voice control and WCAG 2.5.3 (Label in Name). -->
              <span class="flex items-center gap-2">
                <mat-icon class="icon-size-4">{{ showDates() ? 'event_busy' : 'event' }}</mat-icon>
                {{ showDates() ? 'Datum verbergen' : 'Datum tonen' }}
              </span>
            </button>
            <!-- Read-only information, so it sits behind a divider rather than in the run of
                 buttons. At text-hint the one number carrying a fact measured 2.34:1, under AA. -->
            <span
              class="flex items-center gap-1.5 border-l pl-4 ml-1"
              style="border-color: var(--fuse-border)"
              matTooltip="Totale route tijd">
              <mat-icon class="icon-size-4 text-accent">schedule</mat-icon>
              <span class="text-sm text-default">Totaal</span>
              <span class="text-sm font-medium text-default">{{ totalRouteDuration()?.formatted || '--:--' }}</span>
            </span>
            <!-- Map toggle -->
            <button
              type="button"
              mat-icon-button
              (click)="toggleMapSidebar()"
              [attr.aria-label]="mapSidebarVisible() ? 'Verberg kaart' : 'Toon kaart'"
              [matTooltip]="mapSidebarVisible() ? 'Verberg kaart' : 'Toon kaart'">
              <mat-icon>map</mat-icon>
            </button>
          </div>
        </div>
      }
      @if (props.description && !inRouteSection()) {
        <p class="mb-2">{{ props.description }}</p>
      }
      <!-- The mode changes what the field DOES, not just what it shows, and nothing said so. -->
      <!-- text-sm, not text-xs: this project overrides the Tailwind scale (tailwind.config.js
           fontSize), so xs is 10px and sm is 12px. This one line carries the whole behavioural
           model of the field and should not be the smallest text on the page. -->
      @if (!inRouteSection()) {
        <p class="mb-3 text-sm text-accent" [id]="modeHintId">
          @if (showDates()) {
            De route sorteert zichzelf op datum en tijd.
          } @else {
            De dag volgt uit de volgorde van de stops.
          }
        </p>
      }

      <!-- Main content with optional sidebar -->
      <div class="flex gap-4">
        <!-- Left: Form fields with timeline -->
        <div class="flex-1 min-w-0">
          <!-- In a route section the section draws the departure; the way into stop 1 stays here:
               the insert before it and the drive time from the departure. -->
          <!-- Card style draws the "+" before stop 1 always (Figma), also on an empty route and before a
               departure time is known; only the drive time needs that time. -->
          @if (inRouteSection() && (cardMode() || (baseDepartureDate() && (field.fieldGroup?.length ?? 0) >= 1))) {
            @if (cardMode()) {
              <!-- Card style: a zero-height anchor beside the top of stop 1; on an empty route a 10px spacer
                   that makes the gap between departure and destination 34px with the "+" centred in it. -->
              <div
                class="dd-stop-card__insert-first"
                [class.dd-stop-card__insert-first--empty]="canAddStop && !field.fieldGroup?.length"
                [class.dd-stop-card__insert-first--folded]="stopCards()[0]?.collapsed">
                @if (canAddStop) {
                  <button
                    type="button"
                    class="dd-route-insert dd-stop-card__insert dd-stop-card__insert--first"
                    (click)="add(0, getDefaultModelForIndex(0))"
                    aria-label="Locatie invoegen vóór stop 1"
                    matTooltip="Locatie hier invoegen">
                    <mat-icon class="!h-4 !w-4 !text-base !leading-4">add</mat-icon>
                  </button>
                }
                @if (travelTimesBetweenStops()[0]?.formattedDuration; as duration) {
                  <span class="dd-stop-card__travel dd-stop-card__travel--first bg-card px-1 text-[10px] text-accent">{{
                    duration
                  }}</span>
                }
              </div>
            } @else {
              <div class="flex w-12 flex-col items-center gap-1 pb-2">
                @if (canAddStop) {
                  <button
                    type="button"
                    class="dd-route-insert flex h-7 w-9 items-center justify-center rounded-full border-2 border-primary bg-card"
                    (click)="add(0, getDefaultModelForIndex(0))"
                    aria-label="Locatie invoegen vóór stop 1"
                    matTooltip="Locatie hier invoegen">
                    <mat-icon class="!h-4 !w-4 !text-base !leading-4 text-default">add</mat-icon>
                  </button>
                }
                @if (travelTimesBetweenStops()[0]?.formattedDuration; as duration) {
                  <span class="text-[10px] text-accent">{{ duration }}</span>
                }
              </div>
            }
          }
          <!-- Departure point -->
          @if (baseDepartureDate() && !inRouteSection()) {
            <div class="flex items-start gap-3 mb-0">
              <!-- Timeline column: dot + line to first stop -->
              <div class="flex flex-col items-center w-12 flex-shrink-0">
                <!-- Departure dot -->
                <div class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <mat-icon class="!w-2.5 !h-2.5 text-white text-[10px]">location_on</mat-icon>
                </div>
                <!-- Connecting line with travel time -->
                @if (field?.fieldGroup?.length >= 1) {
                  <div
                    class="relative flex-1 w-full flex justify-center"
                    [style.minHeight]="!form.disabled && !field.props.disabled && !hideAddButton() ? '64px' : '40px'">
                    <div class="w-0.5 h-full bg-primary/30"></div>
                    <!-- Insert a route point here. Anchored to the BOTTOM of the gap, just above the
                         travel time and the stop that follows: that is where the new stop appears, so
                         the click lands next to its own result instead of a card-height above it.
                         Centring it in the gap would let it drift ~300px from both markers. -->
                    @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
                      <button
                        type="button"
                        class="dd-route-insert absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex h-7 w-9 items-center justify-center rounded-full border-2 border-primary bg-card"
                        (click)="add(0, getDefaultModelForIndex(0))"
                        aria-label="Locatie invoegen vóór stop 1"
                        matTooltip="Locatie hier invoegen">
                        <!-- The colour sits on the mat-icon itself: Fuse gives .mat-icon its own colour rule
                             (@fuse/tailwind/plugins/utilities.js), so a text-* class on a wrapper never
                             reaches the glyph. -->
                        <mat-icon class="!h-4 !w-4 !text-base !leading-4 text-default">add</mat-icon>
                      </button>
                    }
                    <!-- Travel time sits at the bottom of the gap, next to the stop it leads to,
                         so it never collides with the insert button at the top. -->
                    @if (travelTimesBetweenStops()[0]?.formattedDuration; as duration) {
                      <span class="absolute bottom-1 text-[10px] text-accent bg-card px-1 z-10">{{ duration }}</span>
                    }
                  </div>
                }
              </div>
              <!-- Departure info -->
              <div class="flex-1 pb-2">
                <div class="text-sm font-medium leading-3">Vertrek</div>
                <div class="text-xs text-accent leading-3">
                  {{ baseDepartureDate() | date: 'EEEE d MMM yyyy, HH:mm' : '' : 'nl' }}
                </div>
              </div>
            </div>
          }

          <!-- Route items -->
          @if (field?.fieldGroup?.length >= 1) {
            <div
              class="inline-block w-full"
              cdkDropList
              (cdkDropListDropped)="drop($event)"
              (focusin)="onStopArrivalFocused($event)"
              (focusout)="onStopArrivalBlurred($event)">
              <!-- Object identity, not field.id — see repeat.component.ts for the serialized-id rationale. -->
              @for (fieldGroupField of field.fieldGroup; let i = $index; let last = $last; track fieldGroupField) {
                <!-- With the dates on screen the route orders itself, so this stop's position is a
                     consequence of its time rather than something to be dragged. A stop with no
                     usable time yet is not sorted at all, and keeps its controls: dragging is the
                     only thing that gives it a place. -->
                @let ordersItself = showDates() && (fieldGroupField.model?.['arrival-time'] | sortsOnTime);
                <!-- stopStyle 'card': the stop is an accordion card; stopCards() is empty in row mode. -->
                @let card = stopCards()[i];
                @let locked = form.disabled || field.props.disabled;
                <!-- Card in a route section: the marker column moves into the section's own rail gutter, so the
                     card's left edge lines up with the slot cards (34px rail + 12px gap). -->
                @let railed = card && inRouteSection();
                @if (field.className !== 'hidden') {
                  <div
                    cdkDrag
                    [cdkDragDisabled]="
                      disableDrag || hideAddButton() || ordersItself || form.disabled || field.props.disabled
                    "
                    class="repeater-item mb-0">
                    <!-- The reorder animation transforms THIS element, never the cdkDrag host:
                         CDK writes its own transform there and takes whatever it finds at
                         dragstart as the origin, so animating the host leaves the next drag
                         offset by however far the row had travelled. -->
                    <div class="dd-route-row flex items-stretch gap-3">
                      <!-- Timeline column: dot + time + line to next stop -->
                      <div
                        class="flex flex-col items-center flex-shrink-0"
                        [class.w-12]="!railed"
                        [class.dd-stop-card__gutter]="railed"
                        [class.dd-stop-card__gutter--folded]="railed && card?.collapsed">
                        <!-- Stop dot with number -->
                        @if (railed) {
                          <!-- Figma: the line below the marker takes the accent while the stop is open. -->
                          <span class="dd-stop-card__rail" [class.dd-stop-card__rail--open]="!card?.collapsed" [class.dd-stop-card__rail--last]="last" aria-hidden="true"></span>
                        }
                        @if (card) {
                          @if (railed && !locked && !hideAddButton() && !ordersItself) {
                            <!-- The marker is the drag handle and the keyboard path: Alt+Arrow moves the stop. -->
                            <button
                              type="button"
                              cdkDragHandle
                              [cdkDragHandleDisabled]="disableDrag"
                              class="dd-stop-card__marker dd-stop-card__marker--handle"
                              [class.dd-stop-card__marker--no-drag]="disableDrag"
                              data-cy="route-stop-marker"
                              [attr.aria-label]="'Stop ' + (i + 1) + ' verplaatsen'"
                              matTooltip="Slepen, of Alt + pijl omhoog/omlaag"
                              (keydown.alt.arrowup)="moveUp(i); $event.preventDefault()"
                              (keydown.alt.arrowdown)="moveDown(i); $event.preventDefault()">
                              {{ card.index }}
                            </button>
                          } @else {
                            <div class="dd-stop-card__marker" data-cy="route-stop-marker">{{ card.index }}</div>
                          }
                        } @else {
                          <div
                            class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-on-primary pt-px">
                            {{ i + 1 }}
                          </div>
                        }
                        <!-- Arrival time (and end time if duration set) under the dot. Not beside a card: its
                             header already says the time, and in the gutter it sat on the rail line. -->
                        @if (!railed && calculatedDates().get(i); as pointTime) {
                          <span class="text-[10px] text-accent leading-tight mt-0.5 whitespace-nowrap">
                            {{ pointTime.calculatedDate | date: 'HH:mm' }}
                            @if (getStopEndTime(i); as endTime) {
                              - {{ endTime | date: 'HH:mm' }}
                            }
                          </span>
                        }
                        <!-- Connecting line with travel time -->
                        <div
                          class="relative flex-1 w-full flex justify-center"
                          [style.minHeight]="
                            railed ? null : !form.disabled && !field.props.disabled && !hideAddButton() ? '64px' : '30px'
                          ">
                          <!-- Railed: no floor. The card sets the height, and a folded card is lower than 64px:
                               the floor pushed the "+" below it, onto the next slot's flag. -->
                          @if (!railed) {
                            <div class="w-0.5 h-full bg-primary/30"></div>
                          }
                          <!-- Insert a route point here. Anchored to the BOTTOM of the gap, just above the
                             travel time and the stop that follows: that is where the new stop appears, so
                             the click lands next to its own result instead of a card-height above it.
                             Centring it in the gap would let it drift ~300px from both markers. -->
                          @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
                            @if (railed) {
                              <button
                                type="button"
                                class="dd-route-insert dd-stop-card__insert"
                                [class.dd-stop-card__insert--last]="last"
                                (click)="add(i + 1, getDefaultModelForIndex(i + 1))"
                                [attr.aria-label]="'Locatie invoegen vóór stop ' + (i + 2)"
                                matTooltip="Locatie hier invoegen">
                                <mat-icon class="!h-4 !w-4 !text-base !leading-4">add</mat-icon>
                              </button>
                            } @else {
                              <button
                                type="button"
                                class="dd-route-insert absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex h-7 w-9 items-center justify-center rounded-full border-2 border-primary bg-card"
                                (click)="add(i + 1, getDefaultModelForIndex(i + 1))"
                                [attr.aria-label]="'Locatie invoegen vóór stop ' + (i + 2)"
                                matTooltip="Locatie hier invoegen">
                                <!-- The colour sits on the mat-icon itself: Fuse gives .mat-icon its own colour rule
                                 (@fuse/tailwind/plugins/utilities.js), so a text-* class on a wrapper never
                                 reaches the glyph. -->
                                <mat-icon class="!h-4 !w-4 !text-base !leading-4 text-default">add</mat-icon>
                              </button>
                            }
                          }
                          @if (!last && travelTimesBetweenStops()[i + 1]?.formattedDuration; as duration) {
                            <span
                              class="absolute text-[10px] text-accent bg-card px-1 z-10"
                              [class.bottom-1]="!railed"
                              [class.dd-stop-card__travel]="railed"
                              >{{ duration }}</span
                            >
                          } @else if (last && travelTimeToDestination()?.formattedDuration; as destDuration) {
                            <span
                              class="absolute text-[10px] text-accent bg-card px-1 z-10"
                              [class.bottom-1]="!railed"
                              [class.dd-stop-card__travel]="railed"
                              >{{ destDuration }}</span
                            >
                          }
                        </div>
                      </div>

                      <!-- Order controls. The column is only laid out where reordering is
                           possible at all — a read-only route collapses it, as it always did.
                           Within an editable route it keeps its width even when a stop orders
                           itself, so a sorted stop lines up with an unsorted one. -->
                      @if (!card && !hideAddButton() && !form.disabled && !field.props.disabled) {
                        <div class="flex flex-col items-center w-8 flex-shrink-0 pt-1">
                          @if (!ordersItself) {
                            <!-- Up arrow (not on first item) -->
                            @if (i > 0) {
                              <button
                                type="button"
                                mat-icon-button
                                class="!h-8 !w-8 !min-h-0 !min-w-0"
                                (click)="moveUp(i)"
                                aria-label="Verplaats naar boven"
                                matTooltip="Verplaats naar boven">
                                <mat-icon class="icon-size-5">keyboard_arrow_up</mat-icon>
                              </button>
                            } @else {
                              <div class="h-8 w-8"></div>
                            }

                            <!-- Drag handle -->
                            @if (!disableDrag) {
                              <mat-icon
                                class="text-accent cursor-move"
                                cdkDragHandle
                                svgIcon="mat_outline:drag_indicator">
                              </mat-icon>
                            }

                            <!-- Down arrow (not on last item) -->
                            @if (i < field.fieldGroup.length - 1) {
                              <button
                                type="button"
                                mat-icon-button
                                class="!h-8 !w-8 !min-h-0 !min-w-0"
                                (click)="moveDown(i)"
                                aria-label="Verplaats naar beneden"
                                matTooltip="Verplaats naar beneden">
                                <mat-icon class="icon-size-5">keyboard_arrow_down</mat-icon>
                              </button>
                            } @else {
                              <div class="h-8 w-8"></div>
                            }
                          }
                        </div>
                      }

                      <!-- Field content -->
                      @if (card) {
                        @let open = !card.collapsed;
                        <dd-stop-card
                          class="flex-1 min-w-0"
                          [card]="card"
                          [open]="open"
                          [locked]="locked"
                          [railed]="railed"
                          [last]="last"
                          [position]="i"
                          [showOrder]="!hideAddButton() && !locked && !ordersItself"
                          [inlineOrder]="!inRouteSection()"
                          [canMoveUp]="i > 0"
                          [canMoveDown]="i < field.fieldGroup.length - 1"
                          [dragHandle]="!disableDrag"
                          [removable]="
                            !fieldGroupField.props?.disabled &&
                            props.initialCount < field.fieldGroup.length &&
                            !hideAddButton()
                          "
                          [hasFooterField]="card.footers.length > 0"
                          (toggleOpen)="toggleStop(fieldGroupField)"
                          (moveUp)="moveUp(i)"
                          (moveDown)="moveDown(i)"
                          (remove)="remove(i)">
                          <formly-field [field]="fieldGroupField"></formly-field>
                          @if (calculatedDates().get(i)?.isBelowMinimum) {
                            <div class="text-xs text-warn mt-1">Aankomsttijd is eerder dan einde vorige activiteit</div>
                          }
                          @for (footer of card.footers; track footer) {
                            <formly-field ddStopFooter [field]="footer"></formly-field>
                          }
                        </dd-stop-card>
                      } @else {
                        <div class="flex-1 min-w-0 relative pb-2">
                          <formly-field [field]="fieldGroupField"></formly-field>
                          <!-- Warning if arrival time is before previous activity end -->
                          @if (calculatedDates().get(i); as pointTime) {
                            @if (pointTime.isBelowMinimum) {
                              <div class="text-xs text-warn mt-1">
                                Aankomsttijd is eerder dan einde vorige activiteit
                              </div>
                            }
                          }
                        </div>
                      }

                      <!-- Remove button -->
                      @if (
                        !card &&
                        !form.disabled &&
                        !fieldGroupField.props?.disabled &&
                        props.initialCount < field.fieldGroup.length &&
                        !hideAddButton()
                      ) {
                        <button
                          type="button"
                          class="flex-0 max-h-fit"
                          mat-icon-button
                          aria-label="Routepunt verwijderen"
                          (click)="remove(i)">
                          <mat-icon class="icon-size-4">close</mat-icon>
                        </button>
                      }
                    </div>
                  </div>
                }
              }
            </div>
          } @else if (!inRouteSection()) {
            <p class="text-sm text-accent">Geen {{ props.label ? props.label.toLowerCase() : 'items' }} aangemaakt</p>
          }

          <!-- Destination point -->
          @if (baseDepartureDate() && !inRouteSection()) {
            <div class="flex items-start gap-3">
              <!-- Timeline column: destination dot + time -->
              <div class="flex flex-col items-center w-12 flex-shrink-0">
                <!-- Destination dot -->
                <div class="w-[18px] h-[18px] rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <mat-icon class="!w-2.5 !h-2.5 text-white text-[10px]">flag</mat-icon>
                </div>
                <!-- Arrival time under the dot -->
                @if (destinationTime(); as destTime) {
                  <span class="text-[10px] text-accent leading-tight mt-0.5">
                    {{ destTime | date: 'HH:mm' }}
                  </span>
                  @if (!isSameDay(baseDepartureDate(), destTime)) {
                    <span class="text-[9px] text-warning leading-tight">{{
                      destTime | date: 'd MMM' : '' : 'nl'
                    }}</span>
                  }
                }
              </div>
              <!-- Destination info -->
              <div class="flex-1 pt-1">
                <div class="text-sm font-medium leading-3">Bestemming</div>
                <div class="text-xs text-accent leading-3">
                  @if (destinationTime(); as destTime) {
                    {{ destTime | date: 'EEEE d MMM yyyy, HH:mm' : '' : 'nl' }}
                  } @else {
                    --:--
                  }
                </div>
              </div>
            </div>
          }

          <!-- Add button. A template, so a route section can draw it under its destination slot. -->
          <ng-template #addStop>
            @if (canAddStop) {
              <button
                type="button"
                class="dd-action-button"
                [class.small-button]="!inRouteSection()"
                [class.md-button]="inRouteSection()"
                [class.dd-route-add]="inRouteSection()"
                mat-flat-button
                (click)="add(field.fieldGroup?.length, getDefaultModelForIndex(field.fieldGroup?.length))">
                <mat-icon>add</mat-icon>
                {{ props.addText ? props.addText : 'Locatie toevoegen' }}
              </button>
            }
          </ng-template>
          @if (!inRouteSection() && canAddStop) {
            <div class="pb-4 mt-4 ml-[60px]">
              <ng-container *ngTemplateOutlet="addStop"></ng-container>
            </div>
          }
        </div>

        <!-- Map of a route section: the section draws it full width above the rail and owns the toggle. -->
        <ng-template #sectionMap>
          <app-route-map
            [height]="'h-auto aspect-[1/0.45]'"
            [selectionModeActiveEnabled]="false"
            [locations]="locationsData()"></app-route-map>
        </ng-template>

        <!-- Right: Inline map (alleen in modal context) -->
        @if (!inRouteSection() && isModalContext() && showRouteMap() && mapSidebarVisible()) {
          <div class="w-[400px] flex-shrink-0">
            <app-route-map
              [height]="'h-[400px]'"
              [selectionModeActiveEnabled]="false"
              [locations]="locationsData()"></app-route-map>
          </div>
        }
      </div>
    </div>
  `,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    InfoTooltipComponent,
    RouteMapComponent,
    SortsOnTimePipe,
    StopCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /* Card-style geometry shared with dd-stop-card; the rail sizes (--dd-rail-w/-gap/-row-gap) come from the route section. */
    :host {
      /* Figma Slot: 12px between cards; the "+" sits beside a card, so the gap needs no room for it. */
      --dd-stop-gap: 12px;
      --dd-stop-last-gap: 0px;
      --dd-stop-pill: 28px;
    }

    /* tailwind.scss removes the UA focus ring globally, so every keyboard-reachable control
       needs its own — same treatment as .dd-dropzone in styles/components/_upload.scss.
       --dd-focus-ring is scheme-aware; the brand colour is not, and it is tenant-configurable,
       so it cannot be relied on to contrast with the card underneath. */
    /* Marks the stop that just moved so it stays findable in a long route. */
    .dd-route-row {
      position: relative;
    }

    /* Opaque at all times, not only in flight: two travelling stop cards would otherwise paint
       their fields straight through each other. Permanent because it is a visual no-op at rest
       (the row sits directly on the card) and because a flight-only class needs a cleanup path —
       and transitionend does not fire when a transition is interrupted, which a second sort
       during the first one does. */
    /* Opaque only while travelling: two stop cards crossing would otherwise paint their fields
       straight through each other. Flight-only keeps the resting layout untouched; the cleanup
       runs on transitionend with a timeout as a fallback, because transitionend does not fire
       when a transition is interrupted. */
    .dd-route-row--flying {
      background: var(--fuse-bg-card);
      border-radius: 8px;
    }

    /* The mode toggle changes behaviour, so its ON state has to read at a glance: both states
       were an identical outlined button with only the label differing. */
    /* A stop lays its sub-fields out with floats (w-1/2 float-left). A float row breaks the
       moment one field is taller than its neighbour, and the arrival time is exactly that —
       two inputs plus a DD-MM-YYYY hint. Activiteit then landed to the RIGHT of Locatie on the
       next line instead of starting a fresh row, and Activiteitsduur dropped another line.
       formly-group is a child component, so reaching it needs ::ng-deep; it stays scoped to
       this field by the .dd-route-row ancestor. Floats have no effect on flex items, so the
       existing w-1/2 classes do the dividing on their own. */
    .dd-route-row ::ng-deep formly-group {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      align-content: flex-start;
    }

    /* Figma stylised-number: 30px filled circle, Background/default/Accent with the accent number, no border;
       34px under the card's top edge, below the "+" zone. */
    .dd-stop-card__marker {
      position: relative;
      z-index: 1;
      margin-top: 34px;
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 9999px;
      background: var(--dd-badge-accent-bg);
      color: var(--dd-badge-accent-fg);
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }

    /* As a button the marker must look exactly like the disc: no border, no slab fill; only a keyboard focus ring. */
    .dd-stop-card__marker--handle {
      padding: 0;
      border: 0;
      cursor: grab;
      background: var(--dd-badge-accent-bg) !important;
    }

    .dd-stop-card__marker--no-drag {
      cursor: default;
    }

    .dd-stop-card__marker--handle:focus-visible {
      outline: 2px solid var(--dd-focus-ring) !important;
      outline-offset: 2px;
    }

    /* The line below the marker. Accent while the stop is open, the rail grey when folded (Figma). */
    .dd-stop-card__rail {
      position: absolute;
      top: 64px;
      bottom: 0;
      left: 50%;
      z-index: 1;
      width: 1px;
      background: var(--dd-separator);
    }
    .dd-stop-card__rail--open {
      /* Border/Accent, like the open card's outline: the fg token is the same purple in light but
         near-white in dark, where Figma draws this line blue. */
      background: var(--dd-badge-accent-border);
      /* Figma: an open stop's line ends at the "+" below it; no stub between "+" and the next flag. */
      bottom: calc(var(--dd-stop-gap) + 9px + var(--dd-stop-pill) / 2);
    }
    .dd-stop-card__rail--open.dd-stop-card__rail--last {
      bottom: calc(9px + var(--dd-stop-pill) / 2);
    }

    /* Figma "+ Locatie toevoegen": 36px tall, 16px sides (md-button), 20px plus. */
    .dd-route-add .mat-icon {
      width: 20px;
      height: 20px;
      font-size: 20px;
    }

    /* Figma "+" (Icon button/plus): a bare glyph in the gutter, beside the card's edge, not in the gap.
       The card surface behind it masks the rail line. */
    .dd-stop-card__insert {
      position: absolute;
      bottom: calc(var(--dd-stop-gap) + 9px);
      left: 50%;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--dd-stop-pill);
      height: var(--dd-stop-pill);
      border: 0;
      border-radius: 9999px;
      background: var(--fuse-bg-card);
      transform: translate(-50%, 50%);
    }

    .dd-stop-card__insert .mat-icon {
      color: var(--dd-surface-inverse);
    }

    .dd-stop-card__insert:hover {
      background: color-mix(in srgb, currentColor 8%, var(--fuse-bg-card));
    }

    /* The last card has no margin below it: 9px above its bottom edge (Figma). */
    .dd-stop-card__insert--last {
      bottom: 9px;
    }

    /* Zero-height anchor at the top of stop 1: the "+" is centred 17px below the card's top edge. */
    .dd-stop-card__insert-first {
      /* Centre of the first "+"; the drive time above it follows it. */
      --dd-first-plus: 17px;
      position: relative;
      height: 0;
    }

    /* Empty route: the section adds 12px on each side, so 10px makes Figma's 34px gap. */
    .dd-stop-card__insert-first--empty {
      height: 10px;
    }

    .dd-stop-card__insert--first {
      top: var(--dd-first-plus);
      bottom: auto;
      left: calc(-1 * (var(--dd-rail-gap, 12px) + var(--dd-rail-w, 34px) / 2));
      transform: translate(-50%, -50%);
    }

    .dd-stop-card__insert-first--empty .dd-stop-card__insert--first {
      top: 50%;
    }

    /* Drive time sits in the gutter just above the "+". */
    .dd-stop-card__travel {
      position: absolute;
      bottom: calc(var(--dd-stop-gap) + 9px + var(--dd-stop-pill) / 2 + 4px);
      z-index: 2;
      /* "8u 30m" is wider than the 34px gutter: one line, centred on the rail, beats two. */
      white-space: nowrap;
    }

    /* Above the first "+", like every other drive time: below it the label sat on stop 1's number. */
    .dd-stop-card__travel--first {
      top: calc(var(--dd-first-plus) - var(--dd-stop-pill) / 2 - 2px);
      bottom: auto;
      left: calc(-1 * (var(--dd-rail-gap, 12px) + var(--dd-rail-w, 34px) / 2));
      transform: translate(-50%, -100%);
    }

    .dd-stop-card__gutter {
      position: absolute;
      top: 0;
      bottom: 0;
      left: calc(-1 * (var(--dd-rail-w, 34px) + var(--dd-rail-gap, 12px)));
      width: var(--dd-rail-w, 34px);
    }

    /* Folded card (Figma 4867:186574): the number sits on the card's middle, so the "+" 9px above its
       bottom edge stays clear of it; the line starts under the number. */
    /* The gutter of every stop but the last also spans the gap to the next card: centre on the card. */
    .dd-stop-card__gutter--folded {
      --dd-fold-mid: calc(50% - var(--dd-stop-gap) / 2);
    }
    .dd-stop-card__gutter--folded:has(.dd-stop-card__rail--last) {
      --dd-fold-mid: 50%;
    }
    .dd-stop-card__gutter--folded .dd-stop-card__marker {
      position: absolute;
      top: var(--dd-fold-mid);
      left: 50%;
      margin-top: 0;
      transform: translate(-50%, -50%);
    }
    .dd-stop-card__gutter--folded .dd-stop-card__rail {
      top: calc(var(--dd-fold-mid) + 15px);
    }
    /* Folded, number + two "+" do not fit beside the card's 78px at the open offsets (17/9px): the
       "+" sit on the card's top and bottom edges instead, as Figma draws them. */
    /* The drive time moves up with it: left at the open offset, its masking background cut off the "+". */
    .dd-stop-card__insert-first--folded {
      --dd-first-plus: 4px;
    }
    .dd-stop-card__gutter--folded .dd-stop-card__insert {
      bottom: calc(var(--dd-stop-gap) + 4px);
    }
    .dd-stop-card__gutter--folded .dd-stop-card__insert--last {
      bottom: 4px;
    }
    /* Number and two "+" fill a folded card's gutter: the drive time has no room left there and would
       sit on the number. It shows again when the card opens. */
    .dd-stop-card__gutter--folded .dd-stop-card__travel {
      display: none;
    }

    .dd-route-insert:focus-visible {
      outline: 2px solid var(--dd-focus-ring) !important;
      outline-offset: 2px;
    }

    .custom-placeholder {
      background: #ccc;
      border: dotted 3px #999;
      min-height: 60px;
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
  `,
})
export class FormlyRouteTypeComponent extends FieldArrayType implements OnInit {
  disableDrag = true;
  /**
   * Whether this route may be changed at all.
   *
   * Read live on every use, unlike `disableDrag`, which is stamped once in ngOnInit: a form turns
   * read-only afterwards — a locked entry, a Formly expression flipping props.disabled — and
   * nothing restamps that flag. Everything that writes to the model or to a control asks here.
   */
  private get readOnly(): boolean {
    return !!(this.form?.disabled || this.field.props?.disabled);
  }

  readonly #route = inject(ActivatedRoute);
  readonly #params = toSignal(this.#route.paramMap);
  readonly #moduleId = computed(() => this.options.formState?.entity?.id);
  readonly #userStore = inject(UserStore);
  readonly #userPreferenceStore = inject(UserPreferenceStore);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #injector = inject(Injector);
  readonly #liveAnnouncer = inject(LiveAnnouncer);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #userRolesService = inject(UserRolesService);
  readonly #formService = inject(FormService);
  readonly #destroy$ = injectDestroy();
  readonly #destroyRef = inject(DestroyRef);
  readonly #routeMapService = inject(RouteMapService);
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true }) as DetailsService | null;
  /** Set by an enclosing section; `active` only in a `layout: 'route'` one. */
  readonly #routeSection = inject(RouteSectionSlot, { optional: true });
  readonly inRouteSection = computed(() => this.#routeSection?.owns(this.field) ?? false);
  protected readonly addStopTemplate = viewChild<TemplateRef<unknown>>('addStop');
  readonly #publishAddStop = effect(onCleanup => {
    const slot = this.#routeSection;
    const template = this.addStopTemplate();
    if (!slot || !this.inRouteSection() || !template) return;
    slot.publishAddStop(template);
    onCleanup(() => slot.clearAddStop(template));
  });

  protected readonly sectionMapTemplate = viewChild<TemplateRef<unknown>>('sectionMap');
  readonly #publishMap = effect(onCleanup => {
    const slot = this.#routeSection;
    const template = this.sectionMapTemplate();
    // No locations, no map: an empty map would open for nothing, so the section gets no toggle.
    if (!slot || !this.inRouteSection() || !template || !this.hasMappableLocation()) return;
    slot.publishMap(template);
    onCleanup(() => slot.clearMap(template));
  });

  // ==========================================
  // Configurable field keys (from props, with defaults)
  // ==========================================
  readonly departureDateFieldKey = computed(() => this.props.departureDateField || 'departure-time');
  readonly departureLocationFieldKey = computed(() => this.props.departureLocationField || 'departure-location');
  readonly destinationLocationFieldKey = computed(() => this.props.destinationLocationField || 'destination-location');
  readonly destinationTimeFieldKey = computed(() => this.props.destinationTimeField || 'destination-time');

  // ==========================================
  // State signals
  // ==========================================
  readonly locationsData = signal<unknown[]>([]);
  readonly hideAddButton = signal(false);
  /** `props.stopStyle: 'card'` draws every stop as an accordion card; the default is the timeline row. */
  readonly cardMode = computed(() => this.props.stopStyle === 'card');
  /** Bumped on every form change: card headers read the stops' models and validity, which are not signals. */
  readonly #cardTick = signal(0);
  /**
   * Stops the user collapsed, or that came with an existing entry, keyed by the stop's MODEL object.
   * Formly reuses the slot configs positionally (reorder, insert, remove), so keying on the field
   * config would hand the state to whichever stop moved into the slot. New stops are not in it: they open expanded.
   */
  readonly #collapsedStops = signal<ReadonlySet<object>>(new Set());
  /**
   * Stops whose header the user clicked: from then on their open state is theirs, a submit no longer
   * forces it (a re-submit leaves a stop they collapsed collapsed; its "Incompleet" badge still shows).
   * Known limit: the collapsed set is keyed by model objects taken in ngOnInit, so a model swap
   * (resetModel, rehydration onto the same configs) opens every card.
   */
  readonly #userToggled = new WeakSet<object>();
  /** A submit (markAllAsTouched) shows an invalid collapsed stop. */
  readonly #revealsInvalid = (stop: FormlyFieldConfig): boolean =>
    !!(stop.formControl?.invalid && stop.formControl.touched);
  readonly stopCards = computed<StopCardView[]>(() => {
    if (!this.cardMode()) return [];
    this.#cardTick();
    const times = this.calculatedDates();
    const collapsed = this.#collapsedStops();
    return (this.field.fieldGroup ?? []).map((stop, position) => ({
      ...buildStopCardHeader({
        position,
        model: withoutRoleHidden(stop),
        time: times.get(position)?.calculatedDate ?? null,
        invalid: !!stop.formControl?.invalid,
      }),
      collapsed: collapsed.has(stop.model) && !(this.#revealsInvalid(stop) && !this.#userToggled.has(stop.model)),
      footers: stop.fieldGroup?.filter(child => child.props?.['stopSlot'] === 'footer') ?? [],
    }));
  });
  readonly #localMapVisible = signal(false);
  readonly isModalContext = computed(() => this.#detailsService?.isModal() ?? false);
  readonly mapSidebarVisible = computed(() =>
    this.isModalContext() ? this.#localMapVisible() : this.#routeMapService.sidebarVisible()
  );
  readonly totalRouteDuration = signal<RouteDuration | null>(null);
  readonly baseDepartureDate = signal<Date | null>(null);
  readonly previousDepartureDate = signal<Date | null>(null);
  readonly calculatedDates = signal<Map<number, RoutePointTime>>(new Map());
  readonly #destinationTimeValue = signal<Date | null>(null);

  // Guard to prevent infinite loops during programmatic updates
  #isUpdatingTimes = false;

  // ==========================================
  // Computed
  // ==========================================
  readonly showRouteMap = computed(() => this.locationsData().length > 0);
  /** A route section's map only opens when something can be drawn: an empty stop is a location without coordinates. */
  readonly hasMappableLocation = computed(() => this.locationsData().some(hasMapCoordinates));

  /**
   * Computed signal that calculates travel times between consecutive stops.
   * Includes: departure → first stop, and stop N → stop N+1
   */
  readonly travelTimesBetweenStops = computed<TravelSegment[]>(() => {
    const segments: TravelSegment[] = [];
    const baseDate = this.baseDepartureDate();
    const calcDates = this.calculatedDates();

    if (!baseDate) {
      return segments;
    }

    // Get all route items
    const fieldGroupLength = this.field?.fieldGroup?.length ?? 0;

    for (let i = 0; i < fieldGroupLength; i++) {
      let fromTime: Date | null = null;
      let toTime: Date | null = null;

      if (i === 0) {
        // Departure → first stop
        fromTime = baseDate;
        toTime = calcDates.get(0)?.calculatedDate ?? null;
      } else {
        // Previous stop (end of activity) → current stop
        const prevPointTime = calcDates.get(i - 1);
        if (prevPointTime?.calculatedDate) {
          // Get the end time of previous stop (arrival + duration)
          const prevFieldGroup = this.field.fieldGroup?.[i - 1];
          const prevDuration = prevFieldGroup?.model?.['duration'];

          if (prevDuration) {
            const durationMinutes = DateHelperService.timeStringToMinutes(prevDuration);
            fromTime = DateHelperService.addMinutes(prevPointTime.calculatedDate, durationMinutes);
          } else {
            fromTime = prevPointTime.calculatedDate;
          }
        }
        toTime = calcDates.get(i)?.calculatedDate ?? null;
      }

      if (fromTime && toTime) {
        const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(fromTime, toTime);
        segments.push({
          fromIndex: i === 0 ? -1 : i - 1,
          toIndex: i,
          travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
          formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
        });
      } else {
        segments.push({
          fromIndex: i === 0 ? -1 : i - 1,
          toIndex: i,
          travelMinutes: null,
          formattedDuration: null,
        });
      }
    }

    return segments;
  });

  /**
   * Computed signal that calculates the travel time from the last stop to the destination.
   */
  readonly travelTimeToDestination = computed<TravelSegment | null>(() => {
    const destTime = this.destinationTime();
    if (!destTime) return null;

    const fieldGroupLength = this.field?.fieldGroup?.length ?? 0;
    if (fieldGroupLength === 0) {
      // No stops - travel from departure to destination
      const baseDate = this.baseDepartureDate();
      if (!baseDate) return null;

      const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(baseDate, destTime);
      return {
        fromIndex: -1,
        toIndex: -2, // -2 indicates destination
        travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
        formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
      };
    }

    // Get the end time of the last stop
    const lastIndex = fieldGroupLength - 1;
    const lastPointTime = this.calculatedDates().get(lastIndex);
    if (!lastPointTime?.calculatedDate) return null;

    const lastFieldGroup = this.field.fieldGroup?.[lastIndex];
    const lastDuration = lastFieldGroup?.model?.['duration'];

    let fromTime = lastPointTime.calculatedDate;
    if (lastDuration) {
      const durationMinutes = DateHelperService.timeStringToMinutes(lastDuration);
      fromTime = DateHelperService.addMinutes(lastPointTime.calculatedDate, durationMinutes);
    }

    const travelMinutes = DateHelperService.calculateTimeDifferenceMinutes(fromTime, destTime);
    return {
      fromIndex: lastIndex,
      toIndex: -2, // -2 indicates destination
      travelMinutes: travelMinutes >= 0 ? travelMinutes : null,
      formattedDuration: travelMinutes >= 0 ? DateHelperService.formatDurationMinutes(travelMinutes) : null,
    };
  });

  /**
   * Computed signal that returns the destination time.
   * Uses the backing signal which is updated when the form changes.
   */
  readonly destinationTime = computed<Date | null>(() => this.#destinationTimeValue());

  // ==========================================
  // Lifecycle
  // ==========================================
  ngOnInit() {
    this.disableDrag =
      !this.#userRolesService.checkIfHasEditPermission(this.#userStore.currentUserPermissions(), this.#moduleId()) ||
      this.field.props.disabled;

    // IMPORTANT: Initialize baseDepartureDate FIRST (before adding route items)
    // This ensures that when route items are added via addInitialCount(),
    // the initializeArrivalTimeDate() method has access to the departure date from prefill
    const initialDepartureDate = this.getBaseDepartureDate();
    this.baseDepartureDate.set(initialDepartureDate);
    this.previousDepartureDate.set(initialDepartureDate);

    if (this.cardMode()) {
      // Stops that came filled with the entry start collapsed; an empty one (a fresh create form's
      // first stop, or one holding only defaults) and the ones added later open expanded.
      this.#collapsedStops.set(
        new Set((this.field.fieldGroup ?? []).filter(stop => isStopFilled(stop.model)).map(stop => stop.model))
      );
      // A submit (markAllAsTouched) must reveal an invalid collapsed stop: touched is not a value or status change.
      merge(
        this.field.formControl.valueChanges,
        this.field.formControl.statusChanges,
        this.field.formControl.events.pipe(filter(event => event instanceof TouchedChangeEvent))
      )
        .pipe(takeUntil(this.#destroy$))
        .subscribe(() => this.#cardTick.update(n => n + 1));
    }

    if (this.field.formControl.controls.length === 0) {
      this.addInitialCount();
    }
    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }

    // The setting is remembered per user, so a route can open with the dates already on. The
    // button reads the preference directly and looked right without this, while the fields kept
    // their configured time-only props — correct toggle, no date in sight.
    this.applyDateVisibility();

    // A keeper outlives the reorder that started it, so a route closed mid-animation would
    // leave an observer watching a detached tree.
    this.#destroyRef.onDestroy(() => {
      this.#destroyed = true;
      this.#stopFocusKeeper?.();
      // The destination outlives a hidden route; a validator left behind would read a frozen departure.
      const destinationControl = (this.field?.parent?.fieldGroup ?? []).find(
        f => f.props?.keyPath === this.destinationTimeFieldKey()
      )?.formControl;
      if (destinationControl?.hasValidator(this.#maxDurationValidator)) {
        destinationControl.removeValidators(this.#maxDurationValidator);
        destinationControl.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Watch route field value changes
    this.field.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
      if (this.#isUpdatingTimes) return;
      this.#isUpdatingTimes = true;
      try {
        this.setLocationsData();
        this.calculateRouteDates();
        this.calculateTotalDuration();
      } finally {
        this.#isUpdatingTimes = false;
      }
    });

    // Setup sibling field watchers
    this.setupDateWatchers();

    // Initial calculations - wrapped in guard to prevent infinite loop
    // when calculateRouteDates triggers setValue which triggers watchers
    this.#isUpdatingTimes = true;
    try {
      // If departure date was already set (e.g., via prefill) and we have route items,
      // initialize any empty arrival-times now
      if (initialDepartureDate && this.field.fieldGroup?.length > 0) {
        this.initializeEmptyArrivalTimes(initialDepartureDate);
      }

      this.setLocationsData();
      this.calculateRouteDates();
      this.refreshDestinationTime();
    } finally {
      this.#isUpdatingTimes = false;
    }

    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.#userStore.currentUserId()]);
    }
  }

  // ==========================================
  // Drag & Drop / Reorder
  // ==========================================
  drop(event: CdkDragDrop<string[]>) {
    const modelCopy = this.model;
    moveItemInArray(modelCopy, event.previousIndex, event.currentIndex);
    this.field.formControl.patchValue(modelCopy);
    // patchValue leaves the form pristine; the planner moved this stop, so it has to save (#21026).
    markUserEdit(this.field.formControl);
    this.options.build(this.field);
    // At once, not on the 300ms debounce: every day badge carries the index of the stop it
    // belongs to, and for that window they would name the stop that used to be there.
    this.calculateRouteDates();
  }

  moveUp(index: number): void {
    if (index <= 0) return;
    this.reorderItems(index, index - 1);
    this.calculateRouteDates();
    this.#followMovedStop(index, index - 1);
  }

  moveDown(index: number): void {
    if (index >= this.field.fieldGroup.length - 1) return;
    this.reorderItems(index, index + 1);
    this.calculateRouteDates();
    this.#followMovedStop(index, index + 1);
  }

  /**
   * Keyboard reorder: the button at the old index now holds the other stop, so move focus to the
   * marker of the stop that moved (a second Alt+Arrow then moves the same one) and say so.
   */
  #followMovedStop(from: number, to: number): void {
    this.#liveAnnouncer.announce(`Stop ${from + 1} is nu stop ${to + 1}`, 'polite');
    afterNextRender(
      () => this.#host.nativeElement.querySelectorAll<HTMLElement>('button[data-cy="route-stop-marker"]')[to]?.focus(),
      { injector: this.#injector }
    );
  }

  /** Take every day badge off, so only the ones stamped by this run survive. */
  private clearDayBadges(): void {
    this.field.fieldGroup?.forEach(stop => {
      const props = stop.fieldGroup?.find(f => f.key === 'arrival-time')?.props;
      if (props) {
        props['ddTimeBadge'] = null;
        props['ddTimeBadgeDismissLabel'] = null;
        props['ddTimeBadgeDismiss'] = null;
      }
    });
  }

  /**
   * Put the day a stop landed on inside its time box, or take the badge away again.
   *
   * Only without the dates on screen. With them the planner reads and edits the date itself, so a
   * badge repeating it would be noise.
   */
  private setDayBadge(
    arrivalTimeField: FormlyFieldConfig | undefined,
    index: number,
    dayOffset: number,
    dateLabel: string,
    floor: Date
  ): void {
    if (!arrivalTimeField?.props) {
      return;
    }

    if (this.showDates() || dayOffset <= 0) {
      return;
    }

    arrivalTimeField.props['ddTimeBadge'] = dayOffset === 1 ? '+1 dag' : `+${dayOffset} dagen`;

    // A cross only where pressing it changes something. The same function that performs the
    // removal decides whether it is possible, so the button and the action cannot disagree: a
    // stop held on the next day by the departure time has no way back through here.
    if (removeOneDay(this.model as unknown[], index, floor)) {
      arrivalTimeField.props['ddTimeBadgeDismissLabel'] =
        `Deze stop staat op ${dateLabel}. Haal een dag weg, zet de stop op zijn plek in de route en ` +
        `reken de stops erachter opnieuw door.`;
      arrivalTimeField.props['ddTimeBadgeDismiss'] = () => this.removeDayFromStop(index);
    }

    // Formly only watches props that existed when the child started observing, and this key is
    // stamped later (ngx-formly observeDeep). Without this the badge can go stale whenever the
    // stop's own value does not change — a departure moving under it, say.
    arrivalTimeField.options?.detectChanges?.(arrivalTimeField);
  }

  /**
   * Take the extra day off a stop and put it where that time belongs.
   *
   * Both halves, always. The derived day is a ratchet — `advanceToAtLeast` only pushes forward and
   * `deriveArrival` starts from the stored date — so a stop that reads earlier than the one above
   * it is put straight back on the next day. Writing the earlier time without moving the stop
   * would undo itself before the planner saw it.
   */
  removeDayFromStop(index: number): void {
    // Guarded here, not only in the template: the callback reaches this through a prop, so the
    // markup is not the only way in. A route nobody may edit must not be reordered by it.
    if (this.#destroyed || this.disableDrag || this.readOnly) {
      return;
    }

    const model = this.model;
    if (!Array.isArray(model)) {
      return;
    }

    const removal = removeOneDay(model, index, this.baseDepartureDate());
    if (!removal) {
      return;
    }

    const arrivalControl = this.field.fieldGroup?.[index]?.fieldGroup?.find(f => f.key === 'arrival-time')?.formControl;
    if (!arrivalControl) {
      return;
    }

    // The planner pressed this, so it counts as their edit: the entry has to look unsaved.
    commitUserValue(arrivalControl, removal.arrival);

    // The cascade is anchored on the value just written, never on the model re-read afterwards.
    // Whether Formly has already mirrored a child control's patchValue into the array model is
    // its own business and not documented; depending on that ordering would compute the whole
    // tail from the OLD day and quietly leave every stop behind this one where it was.
    const anchored = [...model];
    anchored[index] = { ...(model[index] as Record<string, unknown>), 'arrival-time': removal.arrival };

    if (removal.index === index) {
      // Only the day came off. Nothing travels, so there is nothing to animate.
      this.applyDayRewrites(rederiveDaysAfter(anchored, index));
      this.calculateRouteDates();
      return;
    }

    // Same FLIP as a sort, and for the same reason: the stop lands somewhere else and a jump cut
    // leaves the planner hunting for where it went. Measured in document coordinates before
    // anything is written, because the animation scrolls between here and the invert.
    const beforeTop = this.rowElements().map(row => row.getBoundingClientRect().top + window.scrollY);
    const newIndexOfOld = [...permutationForMove(model.length, index, removal.index)];

    this.reorderItems(index, removal.index);

    // The stops behind it were pushed forward because this one was. Re-derive their days from the
    // NEW order — after the move, never before, because the order is half of what decides them.
    // Built from the anchored snapshot for the same reason as above.
    const moved = [...anchored];
    const [travelling] = moved.splice(index, 1);
    moved.splice(removal.index, 0, travelling);
    this.applyDayRewrites(rederiveDaysAfter(moved, removal.index));

    this.calculateRouteDates();

    // In this same task, never a setTimeout: detectChanges lays the new order out synchronously
    // so the invert transform is set before the browser can paint the final position.
    this.#cdr.detectChanges();
    // The OLD index: playReorderAnimation looks the row up as newIndexOfOld[editedIndex], so
    // handing it the new slot points at the stop this one displaced — which then flies on top
    // while the stop that actually travelled passes underneath it.
    this.playReorderAnimation(beforeTop, newIndexOfOld, index);
  }

  /**
   * Write re-derived days back, as the planner's own edit.
   *
   * Looked up fresh rather than held across the rebuild: `options.build` keeps a control that is
   * already registered, but the field CONFIG a control hangs off is re-synced positionally, so a
   * reference taken before the move can belong to a different stop after it.
   */
  private applyDayRewrites(rewrites: readonly { index: number; arrival: string }[]): void {
    rewrites.forEach(({ index, arrival }) => {
      const control = this.field.fieldGroup?.[index]?.fieldGroup?.find(f => f.key === 'arrival-time')?.formControl;
      commitUserValue(control, arrival);
    });
  }

  private reorderItems(fromIndex: number, toIndex: number): void {
    const modelCopy = this.model;
    moveItemInArray(modelCopy, fromIndex, toIndex);
    this.field.formControl.patchValue(modelCopy);
    // Every caller is a planner action (arrows, Alt+arrow, removing a day); the self-sort on
    // focusout writes on its own and stays unmarked.
    markUserEdit(this.field.formControl);
    this.options.build(this.field);
  }

  // ==========================================
  // Date visibility, self-ordering & focus
  // ==========================================
  /** Index of the stop whose time field currently has focus. */
  #editedStopIndex: number | null = null;

  /** Where the focus was heading when the reorder started, as "field N of stop M". */
  #pendingFocus: FocusIntent | null = null;

  /** Tears down the focus keeper of the reorder that is currently settling. */
  #stopFocusKeeper: (() => void) | null = null;

  /** The sort runs a tick after focusout, and the entry dialog can close in between. */
  #destroyed = false;

  /** Ties the toggle to its state line. Unique per instance — a page can hold more than one
   *  route field, and a duplicate id would point every button at the first one. */
  readonly modeHintId = 'dd-route-mode-hint-' + Math.random().toString(36).slice(2, 10);

  /**
   * Whether every time in this route also shows its date — a remembered user setting.
   *
   * Safe to restore before the datetime children initialise because `setDateVisibility` stamps
   * `ddTimeOnlyConfigured` before it mutates anything, and `FormlyFieldDateTime` reads that
   * stamp instead of the live props when deciding to attach its "a time must be filled in"
   * validator. Without that decoupling a remembered "on" would silently drop the validator.
   */
  readonly showDates = computed(
    // A route section drops the "Datum tonen" button, so a remembered "on" could not be switched
    // off there: every stop would show a date the design does not have.
    () => !this.inRouteSection() && this.#userPreferenceStore.ui()?.[ROUTE_SHOW_DATES_KEY] === true
  );

  toggleDates(): void {
    this.#userPreferenceStore.editUI({ [ROUTE_SHOW_DATES_KEY]: !this.showDates() });
    this.applyDateVisibility();
    this.calculateRouteDates();
    this.sortByArrivalIfDatesVisible();
  }

  /**
   * Leaving a time field reorders the route. Leaving it — not typing in it.
   *
   * Not the `change` event: an `<input type="time">` fires that the moment HH:MM is complete,
   * with the caret still in the box, and the row slides away mid-edit. Not Formly's value
   * stream either, which runs per keystroke through `ngModelChange`. A focusin / focusout pair
   * is the only thing that means "done with this field".
   *
   * focusin records the stop by INDEX, never by element: change detection re-renders these rows
   * while you type (calculateRouteDates writes back through setValue), and a stale node lookup
   * fails silently — the sort simply never runs.
   */
  onStopArrivalFocused(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null;
    if (!isArrivalInput(input)) {
      return;
    }
    const row = input.closest('.dd-route-row') as HTMLElement | null;
    const index = row ? this.rowElements().indexOf(row) : -1;
    this.#editedStopIndex = index >= 0 ? index : null;
  }

  onStopArrivalBlurred(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null;
    const editedIndex = this.#editedStopIndex;
    this.#editedStopIndex = null;

    if (!isArrivalInput(input) || editedIndex == null) {
      return;
    }

    // Focus moving into an overlay — a date picker, an autocomplete's add-dialog — is not
    // "done". That dialog writes its result back into the control of the SLOT it belongs to,
    // so reordering underneath it lands the value on a different stop.
    // Only a DIFFERENT overlay counts as an interruption. The entry itself opens in a dialog,
    // so the whole route field already lives inside .cdk-overlay-container — testing for that
    // made every click elsewhere in the form look like "focus went into an overlay", and the
    // sort never ran (relatedTarget: MAT-DIALOG-CONTAINER). Comparing PANES is the real
    // question: a date picker or an autocomplete add-dialog opens its own pane on top, and
    // that one writes back into the control of the slot it belongs to.
    const ownPane = input.closest('.cdk-overlay-pane');
    const targetPane = (event.relatedTarget as HTMLElement | null)?.closest('.cdk-overlay-pane');
    if (targetPane && targetPane !== ownPane) {
      return;
    }

    // Tabbing from the date box to the time box of the SAME stop is not leaving the field, it is
    // crossing the seam inside it. Sorting there moves the row out from under a planner who is
    // still halfway through entering the moment. The focusin that follows records the stop again,
    // so the sort still runs when they leave the pair for real.
    const target = event.relatedTarget as HTMLElement | null;
    if (
      isArrivalInput(target as HTMLInputElement | null) &&
      target?.closest('.dd-route-row') === input.closest('.dd-route-row')
    ) {
      return;
    }

    // A tick late on purpose. The typed value reaches the model through ngModelChange ->
    // dateTimeChange -> commitUserValue, which has not run yet when focusout fires; sorting now
    // would read the OLD time and conclude there is nothing to do.
    //
    // No "did the value change?" guard: the sort already stops on its own when the sequence is
    // ascending, so leaving an untouched field costs one no-op. Comparing values here needs the
    // model to be current, which is exactly what it is not at this moment.
    // Where the focus is heading, expressed as "field N of stop M" rather than as an element.
    // The rows keep their DOM position and only their VALUES move, so after a reorder the node
    // that has focus belongs to a different stop — the caret has to travel with the stop.
    this.#pendingFocus = this.describeFocusTarget(event.relatedTarget as HTMLElement | null);

    setTimeout(() => {
      // The dialog can close in the tick between focusout and this callback. Sorting then means
      // patchValue + build on a field whose view is gone.
      if (this.#destroyed) {
        this.#pendingFocus = null;
        return;
      }

      this.sortByArrivalIfDatesVisible(editedIndex);
      // One focusout, one chance. A sort that bails — dates hidden, fewer than two stops, order
      // already right — leaves the hint behind otherwise, and the next reorder from ANY source
      // consumes it. Toggling the dates would then drag the caret into a stop the planner left
      // several actions ago.
      this.#pendingFocus = null;
    }, 0);
  }

  /**
   * With the dates on screen the planner sets them, so the route orders itself by them.
   *
   * Never driven from `valueChanges`: sorting writes through `patchValue`, which would re-enter
   * that subscription. The ascending check inside is what makes a re-entry terminate anyway.
   */
  private sortByArrivalIfDatesVisible(editedIndex = -1): void {
    // The dates toggle stays available in read-only on purpose — it changes what is shown, not
    // what is stored — which makes it a way in that the disabled inputs do not close. disableDrag
    // does not cover it either: that flag is stamped once in ngOnInit, while a form turns
    // read-only afterwards.
    if (!this.showDates() || this.disableDrag || this.hideAddButton() || this.readOnly) {
      return;
    }

    // A drag in flight owns this list. Rebuilding underneath it detaches the row CDK is tracking,
    // and the drop then applies indices taken from the order that existed when the drag started.
    // Standing down costs nothing: the drop patches the model itself, and the next arrival blur
    // sorts whatever is left out of order.
    if (this.#host.nativeElement.querySelector('.cdk-drop-list-dragging')) {
      return;
    }

    const model = this.model;
    if (!Array.isArray(model)) {
      return;
    }

    const reordered = reorderByArrival(model);
    if (!reordered) {
      return;
    }

    // FLIP, first half: where every stop sits right now, by its index in the CURRENT order.
    // Keying on the model object does not survive patchValue — Formly hands back rebuilt
    // objects, so the lookup misses and the animation picks the wrong row to follow.
    // Document coordinates, because the animation scrolls between this measurement and the
    // invert and a viewport-relative delta would be off by exactly that distance.
    const beforeTop = this.rowElements().map(row => row.getBoundingClientRect().top + window.scrollY);

    const newIndexOfOld = [...reordered.newIndexOfOld];

    // Written into the SAME array, never swapped for the new one: patching a fresh array lets
    // Angular write control values back into model objects that are still being read from, which
    // shuffles fields between stops. drop() and reorderItems() write in place for the same reason.
    for (let i = 0; i < model.length; i++) {
      model[i] = reordered.order[i];
    }

    this.field.formControl.patchValue(model);
    this.options.build(this.field);

    // In this same task. detectChanges lays out the new order synchronously, so the invert
    // transform is set before the browser can paint. setTimeout(0) does NOT give that ordering:
    // a macrotask may run after a paint, and a measured trace showed exactly one frame rendered
    // in the FINAL position with no transform on it. That frame is the jump the animation is
    // meant to hide, and it worsens as the route grows and the render takes longer.
    this.#cdr.detectChanges();
    // Follow the stop the planner was editing, not whichever row travelled furthest: those
    // are usually the same row, but when they differ the planner's attention is on the one
    // they just typed in.
    this.playReorderAnimation(beforeTop, newIndexOfOld, editedIndex);
  }

  /**
   * Note where the focus is heading as "field N of stop M", never as an element.
   *
   * The rows keep their DOM position and only their VALUES move, so after a reorder the node
   * that holds the caret belongs to a different stop. A positional description survives the
   * rebuild that an element reference does not.
   */
  private describeFocusTarget(target: HTMLElement | null): FocusIntent | null {
    const row = target?.closest('.dd-route-row') as HTMLElement | null;
    if (!row || !target) {
      return null;
    }
    const rowIndex = this.rowElements().indexOf(row);
    const fields = focusablesIn(row);
    const fieldIndex = fields.indexOf(target);
    // fieldCount is what makes "is the row rebuilt yet" answerable. A row that Formly is halfway
    // through exposes fewer inputs than the stop really has, and index N then points at a
    // different field than the planner clicked.
    return rowIndex >= 0 && fieldIndex >= 0 ? { rowIndex, fieldIndex, fieldCount: fields.length } : null;
  }

  /**
   * Move the caret to wherever the stop it belonged to has landed, and keep it there while
   * Formly finishes rebuilding the rows. See route.focus-keeper.ts for why this is driven by
   * DOM mutations rather than by a timer.
   */
  private restoreFocusAfterReorder(newIndexOfOld: number[]): void {
    // First, unconditionally: the rows have just moved, so a keeper from an earlier reorder is
    // now aiming at a position that holds a different stop. That has to end even when this
    // reorder has no focus to restore of its own.
    this.#stopFocusKeeper?.();
    this.#stopFocusKeeper = null;

    const hint = this.#pendingFocus;
    this.#pendingFocus = null;
    if (!hint || this.#destroyed) {
      return;
    }

    const newRowIndex = newIndexOfOld[hint.rowIndex];
    if (newRowIndex == null || newRowIndex < 0) {
      return;
    }

    // The same field, at the position the stop moved to.
    const landed: FocusIntent = { ...hint, rowIndex: newRowIndex };

    this.#stopFocusKeeper = keepFocusOn(
      this.#host.nativeElement,
      // Re-queried on every call: the rebuild replaces these nodes, so a reference taken now is
      // stale by the pass after next.
      () => intendedField(this.rowElements(), landed)
    );
  }

  /** Rows that carry the reorder animation (inside the cdkDrag host, never the host itself). */
  private rowElements(): HTMLElement[] {
    return Array.from(this.#host.nativeElement.querySelectorAll<HTMLElement>('.dd-route-row'));
  }

  /**
   * Slide each stop from where it was to where it landed.
   *
   * Without this a sort is a jump cut: the row you just edited is somewhere else and nothing
   * says it moved. Invert-then-play means the browser paints the OLD position first, so the
   * motion reads as the stop travelling rather than the list reshuffling.
   */
  private playReorderAnimation(beforeTop: number[], newIndexOfOld: number[], editedIndex = -1): void {
    const rows = this.rowElements();
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Deltas in document space, before anything is written: they stay valid across the scroll
    // below, because scrolling moves the viewport and not the layout.
    const travel = new Map<HTMLElement, number>();
    let traveller: HTMLElement | undefined;
    let furthestRank = -1;

    newIndexOfOld.forEach((newIndex, oldIndex) => {
      const row = rows[newIndex];
      const was = beforeTop[oldIndex];
      if (!row || was == null) {
        return;
      }
      const delta = was - (row.getBoundingClientRect().top + window.scrollY);
      if (delta) {
        travel.set(row, delta);
      }

      // The stop that changed place the most is the one the planner moved; the rest merely
      // shifted up or down to make room. Measured on the INDEX shift, not on pixels: row
      // heights differ, so the biggest pixel delta is not always the biggest move.
      const rank = Math.abs(newIndex - oldIndex);
      if (rank > furthestRank) {
        furthestRank = rank;
        traveller = row;
      }
    });

    if (travel.size === 0) {
      return;
    }

    // The stop the planner was editing is the one to follow. Only when there is none — the
    // toggle sorting the whole route, say — does the furthest traveller stand in for it.
    const editedRow = editedIndex >= 0 ? rows[newIndexOfOld[editedIndex] ?? -1] : undefined;
    const marked = editedRow ?? traveller;
    if (!marked) {
      return;
    }

    // Scroll BEFORE the invert, and instantly. Both measured: a smooth scroll DURING the flight
    // makes the rows that merely shifted cover 2.2x more screen distance than the one that
    // actually moved, so the eye follows the wrong thing; and aiming scrollIntoView at a row
    // still displaced by its own invert transform lands in the wrong place, leaving the edited
    // stop below the fold when the animation ends.
    if (!reduced) {
      marked.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' });
    }

    // Send the caret after the stop it belonged to.
    this.restoreFocusAfterReorder(newIndexOfOld);

    if (reduced) {
      return;
    }

    travel.forEach((delta, row) => {
      // A stop card has no background of its own, so two of them crossing paint their fields
      // straight through each other. Opaque only while in flight, so the resting layout is
      // untouched.
      row.classList.add('dd-route-row--flying');
      // The traveller passes THROUGH the rows it swaps with, so it belongs on top.
      row.style.zIndex = row === marked ? '30' : '1';
      row.style.transition = 'none';
      row.style.transform = 'translateY(' + delta + 'px)';
    });

    requestAnimationFrame(() => {
      travel.forEach((delta, row) => {
        // Constant SPEED, not constant time. At a fixed 320ms a row travelling 2000px moves
        // three times faster than one travelling 660px in the same reorder, which reads as the
        // whole form sliding rather than one stop moving. ~3.6px/ms keeps them coherent.
        const duration = Math.min(700, Math.max(260, Math.round(Math.abs(delta) * 0.28)));

        // Material standard easing, which has an acceleration phase. The previous curve put
        // half the distance into its first 39ms: the travel was over before the eye caught it.
        row.style.transition = 'transform ' + duration + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
        row.style.transform = '';

        const clear = () => {
          row.classList.remove('dd-route-row--flying');
          row.style.zIndex = '';
          row.style.transition = '';
          row.removeEventListener('transitionend', clear);
        };
        row.addEventListener('transitionend', clear);
        // transitionend never fires if the transition is interrupted or never starts.
        setTimeout(clear, duration + 120);
      });
    });
  }

  /**
   * Push the current setting onto every arrival-time field plus the destination-time sibling.
   *
   * Formly observes `props` deeply (observeDeep in ngx-formly-core), so mutating them reaches
   * the OnPush datetime child; `field.options.formState` would not, as it is replaced wholesale
   * on every form value change.
   */
  private applyDateVisibility(): void {
    const show = this.showDates();

    this.field.fieldGroup?.forEach(fieldGroup => {
      const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
      if (arrivalTimeField) {
        this.setDateVisibility(arrivalTimeField, show);
      }
    });

    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());
    if (destTimeField) {
      this.setDateVisibility(destTimeField, show);
    }
  }

  /**
   * Reveal or hide the date half of one datetime field.
   *
   * Only fields CONFIGURED as time-only are touched. A module that already shows its date got
   * no `#requiredTimeValidator` at init, so forcing `hideDate` on it would turn a validated
   * field into one that accepts an empty time. The original setting is recorded on first touch,
   * before anything is mutated, so it survives every later toggle.
   */
  private setDateVisibility(field: FormlyFieldConfig, show: boolean): void {
    if (!field.props) {
      return;
    }
    if (field.props['ddTimeOnlyConfigured'] === undefined) {
      field.props['ddTimeOnlyConfigured'] = !!(field.props.showOnlyTime || field.props.hideDate);
    }
    if (!field.props['ddTimeOnlyConfigured']) {
      return;
    }
    // The template reads `hideDate || showOnlyTime`, so both have to move together.
    field.props.showOnlyTime = !show;
    field.props.hideDate = !show;
  }

  // ==========================================
  // Map Sidebar
  // ==========================================
  toggleMapSidebar(): void {
    if (this.isModalContext()) {
      // In modal: use local state only
      this.#localMapVisible.update(v => !v);
    } else {
      // Normal page: use global sidebar
      this.#routeMapService.toggleSidebar();
    }
  }

  // ==========================================
  // Location Data for Map
  // ==========================================
  #sidebarCleared = false;

  setLocationsData() {
    // Get departure and destination from configured sibling fields
    const departureAddress = this.getSiblingFieldValue(this.departureLocationFieldKey());
    const destinationAddress = this.getSiblingFieldValue(this.destinationLocationFieldKey());

    // Get route points with index for numbering
    const routeLocations =
      this.field.fieldGroup?.map((f, index) => {
        const model = f.model;
        return {
          ...model,
          locationType: 'route-item' as const,
          routeIndex: index + 1, // 1-based numbering for display
        };
      }) ?? [];

    // Build locations array: [departure, ...routePoints, destination]
    const locations: unknown[] = [];

    if (departureAddress) {
      locations.push({
        location: departureAddress,
        locationType: 'departure' as const,
      });
    }

    locations.push(...routeLocations);

    if (destinationAddress) {
      locations.push({
        location: destinationAddress,
        locationType: 'destination' as const,
      });
    }

    this.locationsData.set(locations);
    // The section draws its own map; feeding the global sidebar too would show the route twice.
    if (!this.inRouteSection()) {
      this.#sidebarCleared = false;
      this.#routeMapService.setLocations(locations);
    } else if (!this.#sidebarCleared) {
      // RouteMapService is a root singleton: without this the sidebar keeps the previous entry's route.
      this.#sidebarCleared = true;
      this.#routeMapService.clearLocations();
    }
  }

  // ==========================================
  // Date/Time Calculations
  // ==========================================
  private setupDateWatchers(): void {
    // Use parent form valueChanges for robust change detection
    // This avoids issues with formControl references changing after form rebuilds
    const parentForm = this.field?.parent?.formControl;
    if (parentForm) {
      // Watch all parent form changes and recalculate
      parentForm.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        // Guard against re-entry during programmatic updates
        if (this.#isUpdatingTimes) return;

        this.#isUpdatingTimes = true;
        try {
          const newDepartureDate = this.getBaseDepartureDate();
          const previousDate = this.previousDepartureDate();

          if (newDepartureDate) {
            if (previousDate && previousDate.getTime() !== newDepartureDate.getTime()) {
              // Departure date changed - sync existing times
              this.syncTimesWithDepartureDate(previousDate, newDepartureDate);
            } else if (!previousDate) {
              // Departure date set for the first time - initialize empty arrival-times
              this.initializeEmptyArrivalTimes(newDepartureDate);
            }
          }

          this.previousDepartureDate.set(newDepartureDate);
          this.baseDepartureDate.set(newDepartureDate);
          this.calculateRouteDates();
          this.calculateTotalDuration();
          this.setLocationsData();
          this.refreshDestinationTime();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }

    // Setup watchers for existing route items
    this.setupRouteItemWatchers();
  }

  /**
   * Setup watchers for arrival-time and duration fields within route items.
   * This ensures calculateRouteDates is called when these nested fields change.
   */
  private setupRouteItemWatchers(): void {
    this.field.fieldGroup?.forEach(fieldGroup => {
      this.setupSingleRouteItemWatcher(fieldGroup);
    });
  }

  /**
   * Setup watcher for a single route item's arrival-time and duration fields.
   */
  private setupSingleRouteItemWatcher(fieldGroup: (typeof this.field.fieldGroup)[0]): void {
    const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
    const durationField = fieldGroup.fieldGroup?.find(f => f.key === 'duration');

    if (arrivalTimeField?.formControl) {
      arrivalTimeField.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        if (this.#isUpdatingTimes) return;
        this.#isUpdatingTimes = true;
        try {
          this.calculateRouteDates();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }

    if (durationField?.formControl) {
      durationField.formControl.valueChanges.pipe(debounceTime(300), takeUntil(this.#destroy$)).subscribe(() => {
        if (this.#isUpdatingTimes) return;
        this.#isUpdatingTimes = true;
        try {
          this.calculateRouteDates();
        } finally {
          this.#isUpdatingTimes = false;
        }
      });
    }
  }

  private getBaseDepartureDate(): Date | null {
    const fieldKeyPath = this.departureDateFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const dateField = parentFieldGroup.find(f => f.props?.keyPath === fieldKeyPath);
    const dateValue = dateField?.formControl?.value;

    if (dateValue) {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  calculateRouteDates(): void {
    // Wiped first, stamped per stop below. A run that returns early, or a stop whose time became
    // unreadable, would otherwise leave yesterday's badge on screen with a cross that does
    // nothing when pressed.
    this.clearDayBadges();

    const baseDate = this.baseDepartureDate();
    if (!baseDate) {
      this.calculatedDates.set(new Map());
      this.totalRouteDuration.set(null);
      return;
    }

    const newDates = new Map<number, RoutePointTime>();
    let previousEndTime: Date = baseDate;
    let previousArrivalTime: Date = baseDate;

    this.field.fieldGroup?.forEach((fieldGroup, index) => {
      // Find form controls
      const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
      const durationField = fieldGroup.fieldGroup?.find(f => f.key === 'duration');
      const arrivalTimeControl = arrivalTimeField?.formControl;

      // Read from formControl (not model) to get synced values after departure date change
      const arrivalTimeValue = arrivalTimeControl?.value;
      const durationValue = durationField?.formControl?.value;

      // A stop with no usable time (empty, or the date-only seed a new stop starts with) is
      // left out entirely: it must not advance the running date for the stops behind it.
      const derived = RouteDateUtil.deriveArrival(arrivalTimeValue, previousArrivalTime, this.showDates());
      if (!derived) {
        return;
      }

      const calculatedDateTime = derived.arrival;

      const isNextDay = !DateHelperService.sameDay(calculatedDateTime, baseDate);
      const dayOffset = RouteDateUtil.calendarDaysBetween(baseDate, calculatedDateTime);
      const dateLabel = isNextDay ? DateHelperService.formatDateShortDutch(calculatedDateTime) : '';

      // Calculate minimum time based on previous arrival + duration
      const minimumTime = previousEndTime;
      const isBelowMinimum = calculatedDateTime.getTime() < minimumTime.getTime();

      newDates.set(index, {
        time: derived.time,
        calculatedDate: calculatedDateTime,
        dateLabel,
        isNextDay,
        dayOffset,
        minimumTime,
        isBelowMinimum,
      });

      // Without the dates on screen a stop's day comes from its position, and a time earlier
      // than the one above it silently means "tomorrow". Right for a night run, wrong for a typo,
      // and nothing on screen told the two apart. The badge names the day inside the time box;
      // its cross is the way back (see removeDayFromStop).
      this.setDayBadge(arrivalTimeField, index, dayOffset, dateLabel, baseDate);

      previousArrivalTime = calculatedDateTime;

      // Sync form control with calculated date (for midnight crossing)
      // Only update if the calculated date differs from stored value
      // Use emitEvent: true so datetime-input component updates its internal datePart
      // The badge above is worked out either way, so a read-only route still explains its days —
      // it is only the write-back that stops. Hiding the dates re-derives every stop from the one
      // above it (deriveArrival, trustStoredDate false), which pushes an out-of-order stop a day
      // forward; doing that to an entry nobody may edit rewrites saved planning from a button
      // that is supposed to change the view.
      if (arrivalTimeControl && !this.readOnly) {
        const newValue = DateHelperService.toLocalISOString(calculatedDateTime);
        if (newValue !== arrivalTimeValue) {
          arrivalTimeControl.setValue(newValue);
        }
      }

      // Calculate end time for this stop (arrival + duration)
      let endTime = calculatedDateTime;
      if (durationValue) {
        const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
        endTime = DateHelperService.addMinutes(calculatedDateTime, durationMinutes);
      }
      previousEndTime = endTime;
    });

    this.calculatedDates.set(newDates);
    this.updateDestinationTime(previousEndTime);
    this.calculateTotalDuration();
  }

  /**
   * Update destination time field - ALWAYS sets the DATE portion automatically.
   * The date is calculated based on departure date or last route item (with midnight crossing priority).
   * The TIME portion is only preserved if user has already entered one.
   * If no time entered yet, sets date-only value so user sees "--:--".
   * Since the date field is hidden (hideDate: true), user only sees/enters the time.
   */
  private updateDestinationTime(calculatedEndDate: Date): void {
    // Nothing here but a write: a read-only route keeps the destination time it was saved with.
    if (this.readOnly) {
      return;
    }

    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());

    if (!destTimeField?.formControl) {
      return;
    }

    let currentValue = destTimeField.formControl.value;
    const baseDate = this.baseDepartureDate();

    if (!baseDate) {
      return;
    }

    // Determine the target date - last route item date has priority (accounts for midnight crossings)
    let targetDate: Date;
    if (calculatedEndDate && !isNaN(calculatedEndDate.getTime())) {
      targetDate = new Date(calculatedEndDate.getFullYear(), calculatedEndDate.getMonth(), calculatedEndDate.getDate());
    } else {
      targetDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    }

    if (currentValue instanceof Date) {
      // If current value is a Date object, convert to ISO string
      currentValue = DateHelperService.toLocalISOString(currentValue);
    }

    // Helper: check if value is date-only (no 'T' separator)
    const isDateOnly = (value: string) => value && !value.includes('T');

    // Check if current value has a time component
    const currentHasTime = currentValue && !isDateOnly(currentValue);

    if (currentHasTime) {
      // With the dates on screen the planner owns them, destination included. Recomputing the day
      // here reverts what they just typed about 300ms after the keystroke, with no visible cause —
      // the same fight deriveArrival's trustStoredDate settles for the stops. Time-only keeps
      // deriving the day, because there the order is the only signal available.
      if (this.showDates()) {
        return;
      }

      // User has already entered a time - preserve it, only update date
      const currentDate = new Date(currentValue);
      if (!isNaN(currentDate.getTime())) {
        let newDateTime = new Date(targetDate);
        newDateTime.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0);

        // Midnight crossing: a destination time before the end of the last stop (or before the
        // departure when there are none) is the next day — the same rule the stops follow.
        const lastEnd = calculatedEndDate && !isNaN(calculatedEndDate.getTime()) ? calculatedEndDate : baseDate;
        if (newDateTime.getTime() < lastEnd.getTime()) {
          newDateTime = DateHelperService.addDays(newDateTime, 1);
        }

        const newValue = DateHelperService.toLocalISOString(newDateTime);
        if (newValue !== currentValue) {
          destTimeField.formControl.setValue(newValue, { emitEvent: true });
        }
      }
    } else {
      // No time entered yet - set date-only value (shows "--:--" in UI)
      const dateOnlyValue = DateHelperService.dateObjectToDateString(targetDate);
      if (dateOnlyValue !== currentValue) {
        destTimeField.formControl.setValue(dateOnlyValue, { emitEvent: true });
      }
    }
  }

  /** `maxRouteHours`: a route ending more than this many hours after the departure is invalid. */
  readonly #maxDurationValidator = (control: AbstractControl): ValidationErrors | null => {
    const max = Number(this.props['maxRouteHours']);
    const base = this.baseDepartureDate();
    const value = control.value;
    if (!max || !base || typeof value !== 'string' || !value.includes('T')) return null;
    return new Date(value).getTime() - base.getTime() > max * 3_600_000 ? { maxRouteHours: { max } } : null;
  };

  /**
   * The validator reads the departure too, which is another control: re-run it when the route
   * recalculates, but only when the outcome flips — an unconditional emit would feed the parent
   * valueChanges this component listens to.
   */
  private checkMaxDuration(): void {
    if (!this.props['maxRouteHours']) return;
    const control = (this.field?.parent?.fieldGroup ?? []).find(
      f => f.props?.keyPath === this.destinationTimeFieldKey()
    )?.formControl;
    if (!control) return;
    if (!control.hasValidator(this.#maxDurationValidator)) {
      control.addValidators(this.#maxDurationValidator);
    }
    if (!!this.#maxDurationValidator(control) !== control.hasError('maxRouteHours')) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }
  }

  private calculateTotalDuration(): void {
    this.checkMaxDuration();
    const baseDate = this.baseDepartureDate();
    if (!baseDate) {
      this.totalRouteDuration.set(null);
      return;
    }

    // Try to get destination time from configured field
    const destTimeKeyPath = this.destinationTimeFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === destTimeKeyPath);
    let destTimeValue = destTimeField?.formControl?.value;

    if (destTimeValue instanceof Date) {
      // If current value is a Date object, convert to ISO string
      destTimeValue = DateHelperService.toLocalISOString(destTimeValue);
    }

    // Require eindbestemmingstijd to be filled (with time, not just date)
    // Date-only format: "YYYY-MM-DD" (10 chars, no T)
    // DateTime format: "YYYY-MM-DDTHH:mm..." (contains T)
    if (!destTimeValue || !destTimeValue.includes('T') || destTimeValue.length <= 10) {
      this.totalRouteDuration.set(null);
      return;
    }

    const endTime = new Date(destTimeValue);
    if (isNaN(endTime.getTime())) {
      this.totalRouteDuration.set(null);
      return;
    }

    // Handle midnight crossing: if end time appears to be before start time,
    // it means we've crossed midnight and need to add a day
    let adjustedEndTime = endTime;
    if (endTime.getTime() < baseDate.getTime()) {
      // Check if it's just the time that's earlier (crossed midnight)
      const baseTimeMinutes = baseDate.getHours() * 60 + baseDate.getMinutes();
      const endTimeMinutes = endTime.getHours() * 60 + endTime.getMinutes();

      // If end time is earlier in the day but same or earlier date, add days until it's after baseDate
      if (endTimeMinutes < baseTimeMinutes || DateHelperService.sameDay(baseDate, endTime)) {
        // Calculate how many days to add to make endTime after baseDate
        adjustedEndTime = new Date(endTime);
        while (adjustedEndTime.getTime() <= baseDate.getTime()) {
          adjustedEndTime = DateHelperService.addDays(adjustedEndTime, 1);
        }
      }
    }

    const diffMinutes = DateHelperService.calculateTimeDifferenceMinutes(baseDate, adjustedEndTime);

    if (diffMinutes < 0) {
      this.totalRouteDuration.set(null);
      return;
    }

    this.totalRouteDuration.set({
      totalMinutes: diffMinutes,
      formatted: DateHelperService.formatDurationMinutes(diffMinutes),
    });
  }

  /**
   * Synchronize all route arrival-times and destination time when departure date changes.
   * Keeps the time portion the same, only adjusts the date.
   * Preserves date-only format for values without time (shows "--:--").
   */
  private syncTimesWithDepartureDate(previousDate: Date, newDate: Date): void {
    // Calculate the day difference (in milliseconds)
    const dayDiffMs = this.calculateDateOnlyDifference(previousDate, newDate);

    if (dayDiffMs === 0) {
      return; // No date change, only time changed
    }

    // A datetime control holds the local ISO string the datetime field writes, or a Date where
    // something prefilled it (useCurrentDate, a create page). Both are read as the string, so a Date
    // shifts like any datetime. Calling includes() on the Date threw halfway through, after the
    // stops had moved but before the watcher stored the new departure: every later emission
    // shifted the stops again, and the timeline kept the old departure.
    const asDateTimeString = (value: unknown): string | null => {
      if (value instanceof Date) {
        return DateHelperService.toLocalISOString(value);
      }
      return typeof value === 'string' && value ? value : null;
    };

    // Helper: check if value is date-only (no 'T' separator)
    const isDateOnly = (value: string) => !value.includes('T');

    // Update all route item arrival-times
    this.field.fieldGroup?.forEach(fieldGroup => {
      const arrivalTimeControl = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time')?.formControl;
      const arrivalTimeValue = asDateTimeString(arrivalTimeControl?.value);
      if (arrivalTimeControl && arrivalTimeValue) {
        const wasDateOnly = isDateOnly(arrivalTimeValue);
        const currentDateTime = new Date(arrivalTimeValue);
        if (!isNaN(currentDateTime.getTime())) {
          const newDateTime = new Date(currentDateTime.getTime() + dayDiffMs);
          // Preserve date-only format if original was date-only
          const newValue = wasDateOnly
            ? DateHelperService.dateObjectToDateString(newDateTime)
            : DateHelperService.toLocalISOString(newDateTime);
          arrivalTimeControl.setValue(newValue, { emitEvent: false, onlySelf: true });
        }
      }
    });

    // Update destination time field
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());
    const destTimeValue = asDateTimeString(destTimeField?.formControl?.value);
    if (destTimeField?.formControl && destTimeValue) {
      const wasDateOnly = isDateOnly(destTimeValue);
      const currentDestTime = new Date(destTimeValue);
      if (!isNaN(currentDestTime.getTime())) {
        const newDestTime = new Date(currentDestTime.getTime() + dayDiffMs);
        // Preserve date-only format if original was date-only
        const newValue = wasDateOnly
          ? DateHelperService.dateObjectToDateString(newDestTime)
          : DateHelperService.toLocalISOString(newDestTime);
        destTimeField.formControl.setValue(newValue, { emitEvent: false, onlySelf: true });
      }
    }
  }

  /**
   * Calculate the difference in days between two dates (ignoring time portion).
   * Returns difference in milliseconds.
   */
  private calculateDateOnlyDifference(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d2.getTime() - d1.getTime();
  }

  // ==========================================
  // Helper Methods
  // ==========================================
  private getSiblingFieldValue(fieldKeyPath: string): unknown {
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    return parentFieldGroup.find(f => f.props?.keyPath === fieldKeyPath)?.formControl?.value;
  }

  /**
   * Refresh the destination time signal by reading from the form control.
   * This ensures the timeline updates when destination time is changed.
   */
  private refreshDestinationTime(): void {
    const destTimeKeyPath = this.destinationTimeFieldKey();
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === destTimeKeyPath);
    const destTimeValue = destTimeField?.formControl?.value;

    if (!destTimeValue) {
      this.#destinationTimeValue.set(null);
      return;
    }

    if (destTimeValue instanceof Date) {
      this.#destinationTimeValue.set(destTimeValue);
      return;
    }

    // Check if it's date-only (no time component)
    if (typeof destTimeValue === 'string' && !destTimeValue.includes('T')) {
      this.#destinationTimeValue.set(null);
      return;
    }

    const date = new Date(destTimeValue);
    this.#destinationTimeValue.set(isNaN(date.getTime()) ? null : date);
  }

  getDefaultModelForIndex(index: number): unknown {
    return this.#formService.getDefaultModelForIndex(this.field, index);
  }

  /** Card header click: collapse or expand one stop. Other stops stay as they are. Not in read mode. */
  toggleStop(stop: FormlyFieldConfig): void {
    // Flip what the user sees (same rule as `stopCards`): a stop the submit opened (invalid + touched)
    // collapses on the first click; once the user toggled it, only their own choice counts.
    const shownOpen =
      !this.#collapsedStops().has(stop.model) || (this.#revealsInvalid(stop) && !this.#userToggled.has(stop.model));
    this.#userToggled.add(stop.model);
    this.#collapsedStops.update(set => {
      const next = new Set(set);
      if (shownOpen) next.add(stop.model);
      else next.delete(stop.model);
      return next;
    });
  }

  /**
   * Removing a stop shifts every stop behind it up one slot, and each day badge carries the index
   * of the stop it belongs to. Formly reuses the slot configs positionally, so without this the
   * badges name the previous occupants until the 300ms debounce catches up — and a cross pressed
   * in that window takes a day off the wrong stop.
   */
  override remove(i: number, opts?: { markAsDirty: boolean }): void {
    super.remove(i, opts);
    this.#cardTick.update(n => n + 1);
    this.calculateRouteDates();
  }

  /**
   * Override add() to setup watchers for newly added route items.
   */
  override add(i?: number, initialModel?: unknown, opts?: { markAsDirty: boolean }): void {
    super.add(i, initialModel, opts);
    this.#cardTick.update(n => n + 1);

    // Setup watcher for the newly added item after a small delay to ensure formControl is ready
    setTimeout(() => {
      const insertedIndex = i ?? this.field.fieldGroup.length - 1;

      // Formly's onPopulate always PUSHES the new field object at the end, even for a
      // mid-array insert (ngx-formly-core FieldArrayType.onPopulate). Slots 0..n-1 keep
      // their existing field objects and are re-synced positionally, so the only slot
      // without a watcher is the last one. Watching fieldGroup[insertedIndex] instead
      // would double-subscribe an already-watched control and leave the new slot unwatched.
      const unwatchedFieldGroup = this.field.fieldGroup[this.field.fieldGroup.length - 1];
      if (unwatchedFieldGroup) {
        this.setupSingleRouteItemWatcher(unwatchedFieldGroup);
      }

      // The arrival-time seed IS positional: it must land on the slot we inserted at,
      // because it reads the previous slot's arrival-time + duration.
      const insertedFieldGroup = this.field.fieldGroup[insertedIndex];
      if (insertedFieldGroup) {
        // Initialize arrival-time with calculated date (no time) so user sees "--:--"
        this.initializeArrivalTimeDate(insertedFieldGroup, insertedIndex);
      }

      // Formly builds an inserted stop from the untouched fieldArray template, so it renders
      // time-only regardless of the toggle. Applying it here — after change detection and the
      // child's ngOnInit — keeps the template pristine, which is what leaves the validator on
      // the new field intact.
      if (this.showDates()) {
        this.applyDateVisibility();
      }
    }, 0);
  }

  /**
   * Initialize arrival-time with date-only value for newly added route items.
   * This ensures the user sees "--:--" for time and must fill it in.
   */
  private initializeArrivalTimeDate(fieldGroup: (typeof this.field.fieldGroup)[0], index: number): void {
    const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
    if (!arrivalTimeField?.formControl || arrivalTimeField.formControl.value) {
      return; // Already has a value
    }

    let initialDate: Date | null = null;

    if (index > 0) {
      // Use previous route item's end time (arrival + duration)
      const prevFieldGroup = this.field.fieldGroup[index - 1];
      const prevArrivalTime = prevFieldGroup?.model?.['arrival-time'];
      const prevDuration = prevFieldGroup?.model?.['duration'];

      if (prevArrivalTime) {
        const prevDate = new Date(prevArrivalTime);
        if (!isNaN(prevDate.getTime())) {
          if (prevDuration) {
            const durationMinutes = DateHelperService.timeStringToMinutes(prevDuration);
            initialDate = DateHelperService.addMinutes(prevDate, durationMinutes);
          } else {
            initialDate = prevDate;
          }
        }
      }
    }

    // Fallback to base departure date
    if (!initialDate) {
      initialDate = this.baseDepartureDate();
    }

    if (initialDate) {
      // Set date-only value (format: YYYY-MM-DD) - UI will show "--:--" for time
      const dateOnlyValue = DateHelperService.dateObjectToDateString(initialDate);
      arrivalTimeField.formControl.setValue(dateOnlyValue);
    }
  }

  /**
   * Initialize all empty arrival-times when departure date is set for the first time.
   * This handles the case where route items were added before the departure date was set.
   */
  private initializeEmptyArrivalTimes(departureDate: Date): void {
    let previousEndTime = departureDate;

    this.field.fieldGroup?.forEach((fieldGroup, index) => {
      const arrivalTimeField = fieldGroup.fieldGroup?.find(f => f.key === 'arrival-time');
      const currentValue = arrivalTimeField?.formControl?.value;

      // Only initialize if empty (no value at all)
      if (arrivalTimeField?.formControl && !currentValue) {
        // Set date-only value based on previous end time
        const dateOnlyValue = DateHelperService.dateObjectToDateString(previousEndTime);
        arrivalTimeField.formControl.setValue(dateOnlyValue, { emitEvent: false, onlySelf: true });
      }

      // Update previousEndTime for next iteration
      if (currentValue) {
        const currentDate = new Date(currentValue);
        if (!isNaN(currentDate.getTime())) {
          const durationValue = fieldGroup.model?.['duration'];
          if (durationValue) {
            const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
            previousEndTime = DateHelperService.addMinutes(currentDate, durationMinutes);
          } else {
            previousEndTime = currentDate;
          }
        }
      }
    });

    // Also initialize destination-time if empty
    const parentFieldGroup = this.field?.parent?.fieldGroup ?? [];
    const destTimeField = parentFieldGroup.find(f => f.props?.keyPath === this.destinationTimeFieldKey());
    if (destTimeField?.formControl && !destTimeField.formControl.value) {
      const dateOnlyValue = DateHelperService.dateObjectToDateString(previousEndTime);
      destTimeField.formControl.setValue(dateOnlyValue, { emitEvent: false, onlySelf: true });
    }
  }

  addInitialCount() {
    // Only apply defaultCount when creating a new entry (no existing data)
    if (this.model?.length > 0) {
      return;
    }

    const targetCount = this.props.defaultCount ?? this.props.initialCount;

    if (targetCount) {
      for (let i = 0; i < targetCount; i++) {
        this.add(i, this.getDefaultModelForIndex(i));
      }
    } else {
      this.props.initialCount = 0;
    }
  }

  trackByFn(index: number, item: unknown): number {
    return index;
  }

  /**
   * Helper to check if two dates are on the same day.
   * Used in template to determine if date label should be shown.
   */
  isSameDay(date1: Date | null, date2: Date | null): boolean {
    if (!date1 || !date2) return true;
    return DateHelperService.sameDay(date1, date2);
  }

  /**
   * Get the end time of a stop (arrival time + activity duration).
   * Returns null if no duration is set.
   */
  getStopEndTime(index: number): Date | null {
    const pointTime = this.calculatedDates().get(index);
    if (!pointTime?.calculatedDate) return null;

    const fieldGroup = this.field.fieldGroup?.[index];
    const durationValue = fieldGroup?.model?.['duration'];

    if (!durationValue) return null;

    const durationMinutes = DateHelperService.timeStringToMinutes(durationValue);
    if (durationMinutes <= 0) return null;

    return DateHelperService.addMinutes(pointTime.calculatedDate, durationMinutes);
  }
}
